import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import transactionService from '../../services/transactionService';

const initialState = {
  transactions: [],
  currentTransaction: null,
  stats: null,
  importHistory: [],
  pagination: {
    page: 1,
    totalPages: 1,
    total: 0
  },
  filters: {
    category: '',
    recon_status: '',
    broker: '',
    account_id: '',
    import_batch_id: '',
    startDate: '',
    endDate: '',
    search: ''
  },
  sorting: {
    sortBy: 'transaction_date',
    sortOrder: 'desc'
  },
  importBatchFilter: null,  // Stores the import record for display
  loading: false,
  importing: false,
  error: null,
  importResult: null
};

// Async thunks
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (params, { getState, rejectWithValue }) => {
    try {
      const { filters, pagination, sorting } = getState().transactions;
      const response = await transactionService.getTransactions({
        page: params?.page || pagination.page,
        limit: params?.limit || 50,
        sortBy: params?.sortBy || sorting.sortBy,
        sortOrder: params?.sortOrder || sorting.sortOrder,
        ...filters,
        ...params
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const fetchStats = createAsyncThunk(
  'transactions/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await transactionService.getStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

export const importCSV = createAsyncThunk(
  'transactions/importCSV',
  async ({ file, accountId }, { rejectWithValue }) => {
    try {
      const response = await transactionService.importCMC(file, accountId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Import failed');
    }
  }
);

/**
 * Day 26: parse-and-preview thunk for /import-csv.
 *
 * Renamed from importCSVWithMappings — the old name implied auto-import
 * but after Day 26 the endpoint returns a ParseResult that the caller
 * routes through AIPreviewStep before committing via aiImport.
 *
 * The mapping params (columnMappings, categoryMappings, transformations)
 * are dropped — the parser registry uses fingerprint-based dispatch and
 * doesn't need user-supplied mappings.
 */
export const parseCSVWithRegistry = createAsyncThunk(
  'transactions/parseCSVWithRegistry',
  async ({ file, accountId }, { rejectWithValue }) => {
    try {
      const response = await transactionService.parseCSVWithRegistry(file, accountId);
      // Backend returns the ParseResult directly as the response body
      // (NOT wrapped in .data) per the controller's res.json(...) shape.
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message || 'Parse failed' });
    }
  }
);

// Backward-compat alias for any pre-Day-26 caller. Same async behaviour;
// new shape returned. To be removed once all callers migrated.
export const importCSVWithMappings = parseCSVWithRegistry;

export const approveTransaction = createAsyncThunk(
  'transactions/approveTransaction',
  async (id, { rejectWithValue }) => {
    try {
      const response = await transactionService.approveTransaction(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Approve failed');
    }
  }
);

export const bulkApproveTransactions = createAsyncThunk(
  'transactions/bulkApprove',
  async (transactionIds, { rejectWithValue }) => {
    try {
      const response = await transactionService.bulkApprove(transactionIds);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Bulk approve failed');
    }
  }
);

export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await transactionService.updateTransaction(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Update failed');
    }
  }
);

export const deleteTransaction = createAsyncThunk(
  'transactions/deleteTransaction',
  async (id, { rejectWithValue }) => {
    try {
      await transactionService.deleteTransaction(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Delete failed');
    }
  }
);

export const fetchImportHistory = createAsyncThunk(
  'transactions/fetchImportHistory',
  async (params, { rejectWithValue }) => {
    try {
      const response = await transactionService.getImportHistory(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch import history');
    }
  }
);

const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.importBatchFilter = null;
      state.pagination.page = 1;
    },
    setImportBatchFilter: (state, action) => {
      // Set filter to show transactions from a specific import batch
      state.filters.import_batch_id = action.payload?.id || '';
      state.importBatchFilter = action.payload;
      state.pagination.page = 1;
    },
    clearImportBatchFilter: (state) => {
      state.filters.import_batch_id = '';
      state.importBatchFilter = null;
      state.pagination.page = 1;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setSorting: (state, action) => {
      state.sorting = action.payload;
      state.pagination.page = 1; // Reset to first page when sorting changes
    },
    clearError: (state) => {
      state.error = null;
    },
    clearImportResult: (state) => {
      state.importResult = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions;
        state.pagination = {
          page: action.payload.page,
          totalPages: action.payload.totalPages,
          total: action.payload.total
        };
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch stats
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })

      // Import CSV
      .addCase(importCSV.pending, (state) => {
        state.importing = true;
        state.error = null;
        state.importResult = null;
      })
      .addCase(importCSV.fulfilled, (state, action) => {
        state.importing = false;
        state.importResult = action.payload;
      })
      .addCase(importCSV.rejected, (state, action) => {
        state.importing = false;
        state.error = action.payload;
      })

      // Import CSV with mappings
      .addCase(importCSVWithMappings.pending, (state) => {
        state.importing = true;
        state.error = null;
        state.importResult = null;
      })
      .addCase(importCSVWithMappings.fulfilled, (state, action) => {
        state.importing = false;
        state.importResult = action.payload;
      })
      .addCase(importCSVWithMappings.rejected, (state, action) => {
        state.importing = false;
        state.error = action.payload;
      })

      // Approve transaction
      .addCase(approveTransaction.fulfilled, (state, action) => {
        const index = state.transactions.findIndex(t => t.id === action.payload.transaction.id);
        if (index !== -1) {
          state.transactions[index] = action.payload.transaction;
        }
      })

      // Update transaction
      .addCase(updateTransaction.fulfilled, (state, action) => {
        const index = state.transactions.findIndex(t => t.id === action.payload.transaction.id);
        if (index !== -1) {
          state.transactions[index] = action.payload.transaction;
        }
      })

      // Delete transaction
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.transactions = state.transactions.filter(t => t.id !== action.payload);
      })

      // Import history
      .addCase(fetchImportHistory.fulfilled, (state, action) => {
        state.importHistory = action.payload.imports;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  setImportBatchFilter,
  clearImportBatchFilter,
  setPage,
  setSorting,
  clearError,
  clearImportResult
} = transactionSlice.actions;

export default transactionSlice.reducer;
